import React from 'react';
import Isvg from 'react-inlinesvg';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import { Dropdown, DropdownItem, DropdownMenu } from 'components/Dropdown2';
import Message from 'components/Message';
import { ICONS, PFE_ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { selectIsMobile } from 'selectors/ui';

import { getItemAvailabilityStatus, getItemName, getItemPayoutRate } from '../SkuMenuData.normalizers';
import { itemsGroupMessages, itemsMessages } from '../SkuItems.messages';
import { messages } from './ItemForm.messages';
import SkuItemAvailabilityStatus from './SkuItemAvailabilityStatus';

const { block, element } = bem('sku-item-row');

const ItemActionMenuItems = ({ item, onEditItem, onDeleteItem, closeMenu }) => (
  <>
    {onEditItem && (
      <DropdownItem
        onClick={() => {
          closeMenu();
          onEditItem(item);
        }}>
        <span className={element('icon-wrapper')}>
          <Isvg src={ICONS.editPencilSku} />
        </span>
        <Message message={itemsMessages.editItem} />
      </DropdownItem>
    )}
    {onDeleteItem && (
      <DropdownItem
        onClick={() => {
          closeMenu();
          onDeleteItem(item);
        }}>
        <span className={element('icon-wrapper')}>
          <Isvg src={ICONS.trashCanBlack} />
        </span>
        <Message message={messages.deleteButton} />
      </DropdownItem>
    )}
  </>
);

ItemActionMenuItems.propTypes = {
  item: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
  onEditItem: PropTypes.func,
  onDeleteItem: PropTypes.func,
  closeMenu: PropTypes.func.isRequired,
};

const SkuItemRow = ({ item, currencyCode, onEditItem, onDeleteItem, onStatusChange, onViewItem }) => {
  const intl = useIntl();
  const isMobile = useSelector(selectIsMobile);
  const itemName = getItemName(item);
  const payoutRate = getItemPayoutRate(item?.raw || item);
  const availabilityStatus = getItemAvailabilityStatus(item?.raw || item);
  const isPending = availabilityStatus === null;
  const hasItemActions = Boolean(onEditItem || onDeleteItem);
  const handleRowActivate = onEditItem || onViewItem;
  const isRowInteractive = Boolean(handleRowActivate);

  const buildRowProps = () => {
    if (!isRowInteractive) return {};

    const activate = () => handleRowActivate(item);
    const onKeyDown = event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        activate();
      }
    };

    return {
      onClick: activate,
      onKeyDown,
      role: 'button',
      tabIndex: 0,
      'aria-label': intl.formatMessage(itemsGroupMessages.editItemAriaLabel, { itemName }),
    };
  };

  const interactionProps = buildRowProps();

  return (
    <div
      className={block({ mobile: isMobile, 'no-actions': !hasItemActions, viewable: isRowInteractive })}
      {...interactionProps}>
      <div className={element('name')}>{itemName}</div>
      <div className={element('payout')}>
        {payoutRate === null || !currencyCode ? (
          '––'
        ) : (
          <Message format="currency" currency={currencyCode} value={payoutRate} />
        )}
      </div>
      <div className={element('availability')}>
        {isMobile && hasItemActions ? (
          <SkuItemAvailabilityStatus
            status={availabilityStatus}
            isPending={isPending}
            onChange={onStatusChange ? newStatus => onStatusChange(item, newStatus) : undefined}
            showActionsInMobile
            onEditItem={() => onEditItem?.(item)}
            onDeleteItem={() => onDeleteItem?.(item)}
          />
        ) : (
          <SkuItemAvailabilityStatus
            status={availabilityStatus}
            isPending={isPending}
            onChange={onStatusChange ? newStatus => onStatusChange(item, newStatus) : undefined}
          />
        )}
      </div>
      {!isMobile && hasItemActions && (
        <div className={element('actions')}>
          <Dropdown attachment="right">
            {({ toggleMenu, closeMenu }) => (
              <>
                <button
                  type="button"
                  className={element('actions-trigger')}
                  aria-label={intl.formatMessage(itemsGroupMessages.itemActionsAriaLabel, { itemName })}
                  onClick={toggleMenu}>
                  <Isvg src={PFE_ICONS.threeDots} />
                </button>
                <DropdownMenu>
                  <ItemActionMenuItems
                    item={item}
                    onEditItem={onEditItem}
                    onDeleteItem={onDeleteItem}
                    closeMenu={closeMenu}
                  />
                </DropdownMenu>
              </>
            )}
          </Dropdown>
        </div>
      )}
    </div>
  );
};

SkuItemRow.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    published: PropTypes.bool,
    payoutPrice: PropTypes.shape({
      currencyUnits: PropTypes.number,
      currencyCode: PropTypes.string,
    }),
    raw: PropTypes.shape({}),
  }).isRequired,
  currencyCode: PropTypes.string,
  onEditItem: PropTypes.func,
  onDeleteItem: PropTypes.func,
  onStatusChange: PropTypes.func,
  onViewItem: PropTypes.func,
};

export default SkuItemRow;
